import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { MediaTypeEnum } from '../../../models/enums/results/result-media-type.enum';
import { UnitLocaleEnum } from '../../../models/enums/results/result-units-locale.enum';
import { FeedstockService } from '../../../project/feedstock/feedstock.service';
import { takeUntil } from 'rxjs/operators';
import { FeedstockBreakdownService } from './feedstock-breakdown.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import Utils from '../../../util';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { DropdownDisplayItem } from '../../../models/data-structures/dropdown-display-item';
import { FeedstockBreakdownDataItem } from '../../../models/data-structures/results/feedstock-breakdown/feedstock-breakdown-data-item.model';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-baseline-feedstock-breakdown',
  templateUrl: './baseline-feedstock-breakdown.component.html',
  styleUrls: ['./baseline-feedstock-breakdown.component.scss'],
  providers: [FeedstockService, FeedstockBreakdownService]
})
/**
 * Component for displaying the feedstock breakdown results page. The Feedstock breakdown results can be
 * viewed as both a pie chart and graph.
 */
export class BaselineFeedstockBreakdownComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {
  // data properties

  /**
   * The calculated feedstock breakdown data; consists of a FeedstockBreakdownDataItem for each feedstock in the project.
   */
  feedstockBreakdownData?: FeedstockBreakdownDataItem[] = [];

  // select related properties

  /**
   * An array that contains the selected media types.
   */
  selectedMediaType: string[] = [MediaTypeEnum.TABLE, MediaTypeEnum.PIE_CHART];
  /**
   * An array that contains the available media type; used to populate the media type dropdown.
   */
  mediaTypes: DropdownDisplayItem[] = [];
  /**
   * A string that contains the selected unit type
   */
  selectedUnitsType = UnitLocaleEnum.METRIC;
  /**
   * An array that contains the available options for the unit type (locale); used to populate
   * the unit type dropdown.
   */
  unitsTypes: DropdownDisplayItem[] = [];

  // Table related properties

  /**
   * An array of the table headers for the feedstock breakdown table. These need to match the properties of the
   * FeedstockBreakdownDataItem class.
   */
  tableHeaders: string[] = [];
  /**
   * Data array for the feedstock breakdown table. This array is updated dynamically with translated name to
   * match the table headers.
   */
  tableData: Record<string, FeedstockBreakdownDataItem> = {};
  /**
   * Boolean that determines if the feedstock breakdown table is being shown.
   */
  displayTable = false;

  // graph related properties

  /**
   * Data array for the feedstock breakdown graph. Consists of object that have a name and a value.
   */
  graphData: { name: string, value: number, unit: string}[] = [];
  /**
   * Boolean that determines if the feedstock breakdown pie chart is being shown.
   */
  displayGraph = false;

  precision: number | null = null;

  trigger?: string;

  // notifiers

  /**
   * A notifier that is emitted when the component is destroyed; used to clean up an subscriptions created in
   * this component.
   */
  private onDestroyNotifier = new EventEmitter();

  /**
   * Constructor; injects instances ofo the FeedstockBreakdownService, ConfiguredToastrService.
   * @param feedstockBreakdownService
   * @param toastrService
   * @param translationService
   */
  constructor(private feedstockBreakdownService: FeedstockBreakdownService,
              private toastrService: ConfiguredToastrService,
              private translationService: AnessaTranslationService,
              private projectCommService: ProjectCommunicationService,
  ) {
    super();
  }

  /**
   * Initiates data population.
   */
  ngOnInit(): void {
    if (this.projectCommService.getProjectInfo().defaults?.unitSystem) {
      const unitSystem = this.projectCommService.getProjectInfo().defaults.unitSystem;
      this.selectedUnitsType = unitSystem === 'imperial' ? UnitLocaleEnum.IMPERIAL : UnitLocaleEnum.METRIC;
    } else if (this.projectCommService.getProjectDefaults().gas === '000000000000000000003302') {
      this.selectedUnitsType = UnitLocaleEnum.IMPERIAL
    }
    //this.selectedUnitsType = this.projectCommService.getResultProjectState()?.projectInfo?.defaults?.unitSystem === 'imperial' ? UnitLocaleEnum.IMPERIAL : UnitLocaleEnum.METRIC;
    this.setTranslations();
    this.feedstockBreakdownService.serviceReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        if (loaded) {
          this.precision = this.feedstockBreakdownService.getProjectDecimalPrecision();
          this.populateData();
          this.optionsChanged();
        }
      });
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.setTranslations();
        this.trigger = language;
        this.populateData();
        this.optionsChanged();
      });
  }

  /**
   * Emits the onDestroyNotifier and unsubscribes from it.
   */
  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  setTranslations() {
    this.mediaTypes = [
      new DropdownDisplayItem('RESULTS.GENERAL.PIE_CHART', MediaTypeEnum.PIE_CHART),
      new DropdownDisplayItem('RESULTS.GENERAL.TABLE', MediaTypeEnum.TABLE)
    ];
    this.unitsTypes = [
      new DropdownDisplayItem('RESULTS.GENERAL.METRIC', UnitLocaleEnum.METRIC),
      new DropdownDisplayItem('RESULTS.GENERAL.IMPERIAL', UnitLocaleEnum.IMPERIAL)
    ];
    this.tableHeaders = [
      'COMMON.FEEDSTOCK',
      'RESULTS.GENERAL.AMOUNT',
      'RESULTS.DATA.UNIT',
      'RESULTS.GENERAL.SHARE'
    ];
  }

  /**
   * Checks if all dropdown options are selected. If they are selected, calls the data processing functions to set
   * table and graph data.
   */
  optionsChanged(): void {
    if (this.selectedMediaType && this.selectedUnitsType) {
      this.processData();
    }
  }

  /**
   * Starts the data processing of the all media type that were selected. This function controls if the table and
   * pie chart are shown.
   */
  private processData(): void {
    if (this.feedstockBreakdownData && this.feedstockBreakdownData.length < 1) {
      this.hideAllMedia();
      return;
    }
    if (!this.selectedMediaType || this.selectedMediaType.length < 1) {
      this.hideAllMedia();
      this.tableData = {};
      this.graphData = [];
      return;
    }
    if (this.selectedMediaType.includes(MediaTypeEnum.TABLE)) {
      this.displayTable = true;
      this.processTableData();
    } else {
      this.displayTable = false;
    }
    if (this.selectedMediaType.includes(MediaTypeEnum.PIE_CHART)) {
      this.displayGraph = true;
      this.processPieGraph();
    } else {
      this.displayGraph = false;
    }
  }

  /**
   * Processes the feedstock breakdown data for the table. If conversion to imperial is needed, it is also handled here.
   */
  private processTableData(): void {
    if (!this.feedstockBreakdownData) return;
    let breakdownData: FeedstockBreakdownDataItem[] = Utils.deepCopy(this.feedstockBreakdownData);
    let totalRow: FeedstockBreakdownDataItem | null = Utils.deepCopy(this.feedstockBreakdownService.getFeedstockBreakdownTotal());

    // convert if imperial
    if (this.selectedUnitsType === UnitLocaleEnum.IMPERIAL) {
      breakdownData = this.feedstockBreakdownService.convertFeedstockBreakdownDataToImperial(breakdownData);
      totalRow = this.feedstockBreakdownService.convertFeedstockBreakdownDataToImperial([totalRow])[0];
    }
    this.tableData = {};
    const tempData: Record<string, FeedstockBreakdownDataItem> = {};
    for (const item of breakdownData) {
      tempData[item.feedstock] = item;
    }

    if (totalRow) {
      tempData[totalRow.feedstock] = totalRow;
    }
    this.tableData = tempData;
  }

  /**
   * Processed the feedstock breakdown data for the pie chart. If conversion to imperial is needs, it is also
   * done here. The setTimeout's are needed to force the pie chart component to update the data.
   */
  private processPieGraph(): void {
    setTimeout(() => {
      this.displayGraph = false;
    });
    this.graphData = [];
    let tempData = this.feedstockBreakdownData;
    if (!this.feedstockBreakdownData) return;

    if (this.selectedUnitsType === UnitLocaleEnum.IMPERIAL) {
      tempData = this.feedstockBreakdownService.convertFeedstockBreakdownDataToImperial(this.feedstockBreakdownData);
    }
    if (!tempData) return;

    for (const feed of tempData) {
      const precision = this.feedstockBreakdownService.getProjectDecimalPrecision();
      const amountValue = Utils.roundToPrecision(+Utils.removeCommas(feed.amount), precision);
      const unitName = this.translationService.get(feed.unit);
      this.graphData.push({name: feed.feedstock, value: amountValue, unit: unitName});
    }
    setTimeout(() => {
      this.displayGraph = true;
    });
  }

  /**
   * Populate feedstock breakdown.
   */
  private populateData(): void {
    this.feedstockBreakdownData = this.feedstockBreakdownService.getFeedstockBreakdownData();
    if (!this.feedstockBreakdownData?.length) {
      this.toastrService.error(this.translationService.get('RESULTS.ERRORS.NO_FEEDSTOCK_BREAKDOWN_DATA'));
    }

  }

  /**
   * Hide both the table and the pie chart.
   */
  private hideAllMedia() {
    this.displayTable = false;
    this.displayGraph = false;
  }
}
